import posthog from 'posthog-js';
import { LABS_EXCEPTION_CAPTURE, scrubExceptionEvent } from '../../../shared/exception-scrubber';
import { createSubscriptionEnricher, type GetToken } from '../../../shared/sgapp-subscription';
import { posthogEventName, type AnalyticsEvent } from '../../core/events';

/**
 * The browser-side analytics adapter, and the two UI gates.
 *
 * Two rules, both load-bearing, both copied from packages/landing:
 *
 * 1. **No key, no vendor.** With the key unset PostHog becomes a no-op. CI, local dev and
 *    the browser tests all run without live credentials — which means every one of those
 *    runs also exercises the "analytics is unreachable" configuration.
 * 2. **A vendor cannot break the flow.** Every call is wrapped. A failed capture is a lost
 *    data point; a thrown capture would be a lost upload.
 *
 * ONE VENDOR as of 2026-09-12. Statsig used to resolve the gates and the report experiment
 * and to receive a duplicate copy of every event; all three now come from PostHog, which
 * was already here. See the same note in worker/analytics.ts.
 */

/** The same kill switch the Worker reads. Here it only decides what the page renders. */
export const DISABLE_FLAG = 'preview-objectives-disabled';

/**
 * A kill switch on the upload control alone — read-only without a deploy. Same either/or
 * shape as access: a 240 address is never gated (see `decideAccess` in App.tsx), so this
 * flag only decides for people who got in via the access flag.
 */
export const UPLOAD_GATE = 'preview-objectives-upload';

const REPORT_EXPERIMENT = 'preview-objectives-report';

/** A flat list, best score first. The other arm groups by confidence. */
const DEFAULT_REPORT_VARIANT = 'ranked';

export type ReportVariant = 'ranked' | 'grouped';

export interface Analytics {
  track: (event: AnalyticsEvent) => void;
  ready: boolean;
  gate: (name: string) => boolean;
  reportVariant: () => ReportVariant;
  variant: () => string;
}

const env = import.meta.env;

function swallow(label: string, fn: () => void): void {
  try {
    fn();
  } catch (error) {
    // Deliberately not rethrown. See rule 2 above.
    console.warn(`[analytics] ${label} failed`, error);
  }
}

function initPostHog(userId?: string): ((event: AnalyticsEvent, timestamp?: Date) => void) | null {
  const key = env.VITE_POSTHOG_KEY;
  if (!key) return null;

  posthog.init(key, {
    api_host: env.VITE_POSTHOG_HOST ?? 'https://s.240.org',
    ui_host: 'https://us.posthog.com',
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    capture_pageview: false,
    // Explicit product events only. Replays exclude lesson text, files and account UI.
    autocapture: false,
    disable_session_recording: !userId,
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '.ph-mask',
      blockClass: 'ph-no-capture',
      blockSelector: '.cl-rootBox, .cl-userButtonPopoverCard, input[type="file"]',
      recordHeaders: false,
      recordBody: false,
      recordCrossOriginIframes: false,
      captureCanvas: { recordCanvas: false },
      maskCapturedNetworkRequestFn: (request) => ({
        ...request, name: request.name.split(/[?#]/)[0]!,
      }),
    },
    capture_pageleave: false,
    capture_dead_clicks: false,
    // Errors are on, scrubbed of lesson text first. See shared/exception-scrubber.ts.
    capture_exceptions: LABS_EXCEPTION_CAPTURE,
    before_send: scrubExceptionEvent,
    capture_heatmaps: false,
    capture_performance: false,
    disable_surveys: true,
  });
  if (userId) posthog.identify(userId);
  if (userId) posthog.startSessionRecording();

  return (event, timestamp) => swallow('posthog.capture', () => posthog.capture(posthogEventName(event.name), { ...event.properties, product: 'preview_objectives', event_source: 'browser' }, ...(timestamp ? [{ timestamp }] : [])));
}

/** What runs whenever the keys are absent. */
export function nullAnalytics(): Analytics {
  return {
    track: () => {},
    ready: true,
    // Open by default. A missing PostHog must not hide the product — the Worker's
    // decideAccess is the boundary that actually matters, and it fails closed.
    gate: () => true,
    reportVariant: () => DEFAULT_REPORT_VARIANT,
    variant: () => 'default',
  };
}

let disposeSubscription: (() => void) | undefined;

export async function createAnalytics(userId?: string, getToken?: GetToken): Promise<Analytics> {
  disposeSubscription?.();
  const subscription = createSubscriptionEnricher({ hostname: window.location.hostname });
  subscription.setIdentity(userId ?? null, getToken);
  disposeSubscription = subscription.dispose;
  let capture: ((event: AnalyticsEvent, timestamp?: Date) => void) | null = null;
  swallow('posthog.init', () => {
    capture = initPostHog(userId);
  });

  const live = capture !== null;

  // Flags are read straight off the initialised client. posthog-js resolves them
  // asynchronously after init, so an early read can answer `undefined` — which is why
  // every read below falls back to the permissive value rather than to `false`. A flag
  // that has not arrived yet must not look like a flag that is switched off.
  const flag = (name: string): boolean | string | undefined => {
    if (!live) return undefined;
    try {
      return posthog.getFeatureFlag(name) ?? undefined;
    } catch {
      return undefined;
    }
  };

  const reportVariant = (): ReportVariant =>
    flag(REPORT_EXPERIMENT) === 'grouped' ? 'grouped' : DEFAULT_REPORT_VARIANT;

  return {
    ready: true,

    track: (event) => {
      if (capture) subscription.track(event, (enriched, timestamp) => capture?.({ ...event, properties: enriched.properties }, timestamp));
    },

    // Open unless PostHog says otherwise, for the reason in nullAnalytics: a missing or
    // slow vendor must not hide the product. The Worker's own check is the boundary that
    // decides access, and that one fails closed.
    gate: (name) => flag(name) !== false,

    reportVariant,
    variant: reportVariant,
  };
}

/** Correlation only; the Worker still derives the user from the verified Clerk token. */
export function posthogHeaders(): Record<string, string> {
  if (!env.VITE_POSTHOG_KEY) return {};
  try {
    const distinctId = posthog.get_distinct_id();
    const sessionId = posthog.get_session_id();
    return distinctId && sessionId
      ? { 'X-POSTHOG-DISTINCT-ID': distinctId, 'X-POSTHOG-SESSION-ID': sessionId }
      : {};
  } catch { return {}; }
}

/** Reset on account changes; retain the anonymous ID only when leaving the public sample. */
export function stopAnalytics({ resetIdentity = true }: { resetIdentity?: boolean } = {}): void {
  disposeSubscription?.();
  disposeSubscription = undefined;
  if (!env.VITE_POSTHOG_KEY) return;
  swallow('stop', () => { posthog.stopSessionRecording(); if (resetIdentity) posthog.reset(); });
}
